/**
 * Lightweight GA4 wrapper.
 *
 * GA4 only loads after the user accepts analytics cookies (see index.html).
 * Until then, `window.gtag` exists but `analytics_storage` is denied —
 * events are buffered/dropped by GA. Calling `trackEvent` is always safe.
 *
 * See `docs/analytics-events.md` for the full event taxonomy.
 */

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
  }
}

export type GAEventName =
  // Conversions
  | "cta_click"
  | "sign_up"
  | "login"
  | "contact_submit"
  | "contact_submit_error"
  // Activation (onboarding funnel)
  | "onboarding_complete"
  | "onboarding_step_completed"
  | "first_prospect_created"
  | "first_organization_created"
  | "first_search_run"
  // Engagement
  | "nav_link_click"
  | "external_link_click"
  | "pricing_view";

export interface GAEventParams {
  /** Where the event was triggered (e.g. "hero", "footer", "navbar"). */
  location?: string;
  /** Human-readable label (button text, form name, etc.). */
  label?: string;
  /** Logical method (e.g. "email", "google"). */
  method?: string;
  /** Free-form metadata (kept short — GA4 limits param size). */
  [key: string]: unknown;
}

const CONSENT_STORAGE_KEY = "thiia_cookie_consent";
const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;

/**
 * Inspect cookie consent + gtag readiness. Returns a structured reason
 * explaining whether a GA4 event will reach Google or be silently dropped.
 */
export function getAnalyticsStatus(): {
  ok: boolean;
  reason:
    | "ok"
    | "ssr"
    | "consent_missing"
    | "consent_denied"
    | "consent_invalid"
    | "gtag_not_loaded";
  detail?: string;
} {
  if (typeof window === "undefined") return { ok: false, reason: "ssr" };

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch (e) {
    return { ok: false, reason: "consent_invalid", detail: String(e) };
  }
  if (!raw) {
    return { ok: false, reason: "consent_missing", detail: "Cookie banner not answered yet." };
  }
  try {
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    if (parsed?.analytics !== true) {
      return {
        ok: false,
        reason: "consent_denied",
        detail: `analytics flag = ${JSON.stringify(parsed?.analytics)}`,
      };
    }
  } catch (e) {
    return { ok: false, reason: "consent_invalid", detail: String(e) };
  }

  if (typeof window.gtag !== "function") {
    return {
      ok: false,
      reason: "gtag_not_loaded",
      detail: "Consent granted but the GA4 script tag has not finished loading yet.",
    };
  }
  return { ok: true, reason: "ok" };
}

function logSuppressed(name: string, status: ReturnType<typeof getAnalyticsStatus>): void {
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.info(
    `[GA4] event "${name}" not sent — reason: ${status.reason}` +
      (status.detail ? ` (${status.detail})` : ""),
  );
}

export function trackEvent(name: GAEventName, params: GAEventParams = {}): void {
  try {
    const status = getAnalyticsStatus();
    if (!status.ok) {
      logSuppressed(name, status);
      return;
    }
    window.gtag!("event", name, params);
    if (isDev) {
      // eslint-disable-next-line no-console
      console.debug(`[GA4] event "${name}" sent`, params);
    }
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn(`[GA4] event "${name}" threw`, e);
    }
    /* analytics must never break the app */
  }
}

/** Convenience: track a CTA click in one call. */
export function trackCtaClick(location: string, label: string): void {
  trackEvent("cta_click", { location, label });
}

/**
 * Track a click on a secondary navigation link (header, footer, sidebar, legal).
 * `to` is the destination href (relative or absolute).
 */
export function trackNavClick(
  location: "header" | "footer" | "sidebar" | "mobile_nav" | "legal" | (string & {}),
  label: string,
  to: string,
): void {
  trackEvent("nav_link_click", { location, label, to });
}

/**
 * Track a click on a link that leaves the app (external domain, mailto:, tel:).
 */
export function trackExternalLinkClick(
  location: string,
  label: string,
  href: string,
): void {
  trackEvent("external_link_click", { location, label, href });
}

/**
 * Fire a GA4 event only the first time it occurs for a given user (per browser).
 * Uses `localStorage` to persist the "already fired" flag across sessions, so
 * activation milestones (first prospect, first search, etc.) are not double-counted.
 *
 * Returns `true` if the event was fired, `false` if it was suppressed.
 */
export function trackFirstEventOnce(
  name: GAEventName,
  params: GAEventParams = {},
  storageKey?: string,
): boolean {
  try {
    if (typeof window === "undefined") return false;
    const key = `thiia_ga_first_${storageKey ?? name}`;
    if (window.localStorage.getItem(key)) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.info(`[GA4] event "${name}" not sent — reason: already_fired (key: ${key})`);
      }
      return false;
    }
    window.localStorage.setItem(key, String(Date.now()));
    trackEvent(name, params);
    return true;
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn(`[GA4] trackFirstEventOnce("${name}") threw`, e);
    }
    return false;
  }
}
